import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Smartphone, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { devError } from '../utils/logger';
import { useAuth } from '../App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Pair flow.
 *
 * Phone is here because the user scanned a QR or typed their pair code.
 * If `?code=XXXXXX` is in the URL we auto-claim. Otherwise we show a 6-box
 * code input.
 */
export default function WingmanPairPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get('code') || '').replace(/\D/g, '').slice(0, 6));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const claim = async (theCode) => {
    if (!theCode || theCode.length !== 6) {
      setError('Enter the 6-digit pair code shown on your desktop.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/wingman/pair-claim`, { code: theCode });
      if (setUser) setUser(data.user);
      toast.success('Phone paired ✓');
      navigate('/wingman/inbox', { replace: true });
    } catch (e) {
      devError('pair claim failed', e);
      const msg = e.response?.data?.detail || 'Pair failed. Generate a new code on desktop and try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (params.get('code') && code.length === 6 && !submitting) claim(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#0F172A] text-white px-6 py-12 flex flex-col items-center">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
            <Smartphone className="w-7 h-7 text-[#D4AF37]" />
          </div>
        </div>
        <h1 className="text-center text-2xl font-bold text-[#D4AF37] mb-1">Pair your phone</h1>
        <p className="text-center text-sm text-slate-400 mb-8">
          On your desktop, open <b className="text-slate-200">Supplier Dashboard → Pair phone</b> and scan the QR — or type the 6-digit code below.
        </p>

        <CodeInput value={code} onChange={setCode} disabled={submitting} />

        {error && <p className="mt-3 text-xs text-red-400 text-center" data-testid="wingman-pair-error">{error}</p>}

        <button
          onClick={() => claim(code)}
          disabled={submitting || code.length !== 6}
          className="mt-6 w-full py-3 rounded-full bg-[#D4AF37] text-black font-semibold disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="wingman-pair-submit"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          Pair this phone
        </button>

        <p className="text-[11px] text-slate-500 text-center mt-6">
          Pair codes expire after 5 minutes and can only be used once.
        </p>
      </div>
    </div>
  );
}

function CodeInput({ value, onChange, disabled }) {
  const handle = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 6);
    onChange(v);
  };
  return (
    <div className="relative">
      <input
        type="tel"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        value={value}
        onChange={handle}
        disabled={disabled}
        className="w-full bg-transparent text-center tracking-[0.6em] text-3xl font-mono py-4 border-2 border-white/10 rounded-2xl focus:outline-none focus:border-[#D4AF37] caret-[#D4AF37]"
        placeholder="● ● ● ● ● ●"
        maxLength={6}
        data-testid="wingman-pair-code-input"
      />
    </div>
  );
}
